import React from 'react';
import { cs } from '@bbkAdminUtils/css-utils';

export type PricingPageOverlayLoaderProps = {
  header?: string;
  subheader?: string;
};

const hasCopy = (value: string | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Full-page loader overlay shown/hidden on demand via the merchant-triggered `SHOW_LOADER` event
 * (cb-pricify-js `Pricify.showLoader(true)` or `Pricify.showLoader({ header, subheader })`).
 * Sits on top of the entire pricing page while visible.
 */
export const PricingPageOverlayLoader: React.FC<
  PricingPageOverlayLoaderProps
> = ({ header, subheader }) => {
  const showHeader = hasCopy(header);
  const showSubheader = hasCopy(subheader);
  const ariaLabel = showHeader ? header.trim() : 'Loading';

  return (
    <div
      className={cs(
        'tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-p-6',
        'tw-bg-[color-mix(in_srgb,var(--pricing-page-text-font-text-color,#1f2937)_25%,transparent)]',
        'tw-backdrop-blur-[2px]'
      )}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
    >
      <div
        className={cs(
          'tw-flex tw-w-full tw-max-w-sm tw-flex-col tw-items-center tw-gap-5 tw-px-8 tw-py-10 tw-text-center',
          'tw-rounded-pricingtable-border-radius tw-border tw-shadow-xl',
          'tw-border-[color-mix(in_srgb,var(--pricingtable-style-accent-color,#6366f1)_18%,transparent)]',
          'tw-bg-[var(--pricing-page-card-background-color,#ffffff)]',
          'tw-ring-1 tw-ring-[color-mix(in_srgb,var(--pricing-page-text-font-text-color,#1f2937)_6%,transparent)]'
        )}
      >
        <div
          className={cs(
            'tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full',
            'tw-bg-[color-mix(in_srgb,var(--pricingtable-style-accent-color,#6366f1)_10%,transparent)]'
          )}
          aria-hidden
        >
          <div
            className={cs(
              'tw-h-9 tw-w-9 tw-animate-spin tw-rounded-full tw-border-[3px]',
              'tw-border-[color-mix(in_srgb,var(--pricingtable-style-accent-color,#6366f1)_20%,transparent)]',
              'tw-border-t-[var(--pricingtable-style-accent-color,#6366f1)]'
            )}
          />
        </div>

        {(showHeader || showSubheader) && (
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            {showHeader ? (
              <p
                className={cs(
                  'tw-m-0 tw-text-lg tw-font-semibold tw-leading-snug tw-tracking-tight',
                  'tw-text-[var(--pricing-page-text-font-text-color,#1f2937)]'
                )}
              >
                {header.trim()}
              </p>
            ) : null}
            {showSubheader ? (
              <p
                className={cs(
                  'tw-m-0 tw-text-sm tw-leading-relaxed',
                  'tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color,#1f2937)_65%,transparent)]'
                )}
              >
                {subheader.trim()}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
